import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../services/api';
import type {
  ExecutiveSummaryResponse,
  GenerateSummaryRequest,
  InsufficientDataError,
} from './useExecutiveSummary';
import { isInsufficientDataError, getInsufficientDataDetails, isCachedResponse } from './useExecutiveSummary';

/**
 * Hook to fetch the executive summary for a target user (admin only).
 */
export const useAdminExecutiveSummary = (userId: string | undefined) => {
  return useQuery<ExecutiveSummaryResponse, Error>({
    queryKey: ['adminExecutiveSummary', userId],
    queryFn: async () => {
      const response = await adminApi.getUserExecutiveSummary(userId!);
      return response.data as ExecutiveSummaryResponse;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Hook to generate/regenerate an executive summary for a target user (admin only).
 */
export const useAdminGenerateSummary = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ExecutiveSummaryResponse, Error, GenerateSummaryRequest>({
    mutationFn: async (request: GenerateSummaryRequest = {}) => {
      const response = await adminApi.generateUserExecutiveSummary(userId!, request);
      return response.data as ExecutiveSummaryResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['adminExecutiveSummary', userId], data);
      queryClient.invalidateQueries({ queryKey: ['adminExecutiveSummary', userId] });
    },
  });
};

export type { ExecutiveSummaryResponse, GenerateSummaryRequest, InsufficientDataError };
export { isInsufficientDataError, getInsufficientDataDetails, isCachedResponse };

